/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { GraduationCap, X } from 'lucide-react';
import { Button } from './ui/button';

interface TheoryPrimerProps {
  onDismiss?: () => void;
}

const TheoryPrimer: React.FC<TheoryPrimerProps> = ({ onDismiss }) => {
  return (
    <Card className="mb-4 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-950/20 dark:to-purple-950/20 border-blue-200 dark:border-blue-800">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <CardTitle className="text-lg">
              Learn Music Theory Through Visualization
            </CardTitle>
          </div>
          {onDismiss && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              className="h-6 w-6 p-0 ml-4"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-1">
            What you're seeing:
          </h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            This isn't just a colorful diagram—it's an interactive map of music theory on your guitar.
            Each colored dot represents a specific note and its role (interval) in the scale or chord.
            The colors help you recognize patterns that repeat across the entire fretboard.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-1">
            How to learn with this:
          </h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Start by playing the notes in sequence—hear how they sound together.
            Notice how the same pattern appears in multiple positions.
            Try picking different keys and scales to see how patterns shift.
            Use the right panel to explore related chords and different chord fingerings.
            Got questions? Tap the chat bubble on the bottom right to ask the Harmonic Companion—your guide for guitar and theory questions.
            Want to learn from the pros? Check out the Contributors section in the top-right corner to explore content from professional guitarists and instructors.
            The goal is to understand relationships, not just memorize positions.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-300 mb-1">
            Learning tip:
          </h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Focus on one position at a time. Once you can play it smoothly and understand which note is which interval,
            move to the next position. This is how professional guitarists think—not as individual notes, but as moveable patterns.
            Feeling overwhelmed by all the notes? In Scale mode, try the <strong>play button</strong> in the Notes and Intervals section to see patterns one note at a time.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default TheoryPrimer;
